// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import Select from 'presentations/views/components/atoms/Select';
import TextField from 'presentations/views/components/atoms/TextField';
import TextArea from 'presentations/views/components/atoms/TextArea';
import ExpandFormField from 'presentations/views/components/molecules/ExpandFormField';
import Button from 'presentations/views/components/atoms/Button';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Icon from 'presentations/views/components/atoms/Icon';
import ColorButton from 'presentations/views/components/atoms/ColorButton';
import TimeZoneListPopover from 'presentations/views/components/organisms/TimeZoneListPopover';
import {
  spacing,
  borderRadius,
  mediaMax,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import { SyntheticEvent, useRef, useState } from 'react';
import { ErrorList } from 'domains/models';
import { nativeName } from 'domains/models/user_setting/locale';
import { gray03, primary, white } from 'presentations/views/styles/color';
import ColorList, {
  OriginalColorPicker,
} from 'presentations/views/components/organisms/ColorList';
import { helpPath, userPath } from 'presentations/routes/path';

interface Props {
  isLoading?: any;
  onChange?: any;
  onChangeOriginalColor: (color: string) => void;
  onSubmit?: any;
  onExpand?: any;
  expanded: string | null;
  onChangeProfileImage?: any;
  onDeleteProfileImage?: any;
  errors: ErrorList;
  locale: string | null;
  locales: any[];
  color: string | null;
  originalColor: string;
  colors: any[];
  timeZone: string | null;
  profileImage: string | null;
  profileImageFile: File | null;
  slug: string;
  profile: string | null;
  teamSlug: string;
  onSelectTimeZone: (name: string) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [timeZoneAnchor, setTimeZoneAnchor] = useState<HTMLElement | null>(
    null,
  );
  const fileInput = useRef<HTMLInputElement>(null);

  const onClickImage = (e: SyntheticEvent) => {
    if (fileInput.current !== null) {
      fileInput.current.click();
    }
  };

  const onChangeImage = (e: SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.files) return;
    const file = e.currentTarget.files[0];
    const reader = new FileReader();
    reader.addEventListener('load', (event: any) => {
      const url = String(event.target.result);
      setFileUrl(url);
      props.onChangeProfileImage(file);
    });
    reader.readAsDataURL(file);
  };

  const onCloseTimeZone = () => {
    setTimeZoneAnchor(null);
  };

  const onClickTimeZone = (e: SyntheticEvent<HTMLDivElement>) => {
    setTimeZoneAnchor(e.currentTarget);
  };

  const onSelectTimeZone = (name: string) => {
    props.onSelectTimeZone(name);
    onCloseTimeZone();
  };

  const onDeleteProfileImage = (e: SyntheticEvent) => {
    e.stopPropagation();
    setFileUrl(null);
    props.onDeleteProfileImage();
  };

  const hasImage = fileUrl || props.profileImage;

  return (
    <Container>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'profileImage'}
          label={t('profileImage')}
          description={t('profileImage description')}
          name="profileImage"
          onExpand={props.onExpand}
          help={{
            url: helpPath('7155196'),
          }}
          summary={
            <Avatar
              src={fileUrl || props.profileImage}
              style={{ width: 80, height: 80 }}
            />
          }
          content={
            <ProfileImage onClick={onClickImage}>
              <FileInput
                name="profileImage"
                ref={fileInput}
                type="file"
                onChange={onChangeImage}
                accept="image/*"
              />
              <UploadAvatar>
                {(fileUrl || props.profileImage) && (
                  <Avatar
                    src={fileUrl || props.profileImage}
                    style={{ width: 80, height: 80 }}
                  />
                )}
                <UploadIcon color="inherit" name="camera_alt" />
              </UploadAvatar>
              {hasImage && (
                <DeleteIconButton onClick={onDeleteProfileImage}>
                  <Icon name={'delete'} />
                </DeleteIconButton>
              )}
            </ProfileImage>
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['profileImage'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('profileImage')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'slug'}
          label={t('slug')}
          name="slug"
          description={t('slug description')}
          onExpand={props.onExpand}
          summary={userPath(props.teamSlug, props.slug) || t('no setting')}
          content={
            <FieldWithPrefix>
              <Prefix>{`/t/${props.teamSlug}/users/`}</Prefix>
              <TextField
                name={'slug'}
                value={props.slug}
                onChange={props.onChange}
                errors={props.errors['slug']}
                placeholder={t('slug placeholder')}
              />
            </FieldWithPrefix>
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['slug'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('slug')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'profile'}
          label={t('profile')}
          name="profile"
          description={t('profile description')}
          onExpand={props.onExpand}
          summary={props.profile || t('no setting')}
          content={
            <TextArea
              name={'profile'}
              value={props.profile}
              onChange={props.onChange}
              errors={props.errors['profile']}
              placeholder={t('profile placeholder')}
              isFullWidth
            />
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['profile'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('profile')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'timeZone'}
          label={t('timeZone')}
          name="timeZone"
          description={t('timezone description')}
          onExpand={props.onExpand}
          summary={props.timeZone}
          content={
            <TimeZoneContainer onClick={onClickTimeZone}>
              <TimeZoneSelect
                name="timeZone"
                value={props.timeZone}
                isNative
                options={[
                  {
                    value: props.timeZone,
                    text: props.timeZone,
                  },
                ]}
              />
            </TimeZoneContainer>
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['timeZone'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('timeZone')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'locale'}
          label={t('locale')}
          name="locale"
          description={t('locale description')}
          onExpand={props.onExpand}
          summary={props.locale && nativeName(props.locale)}
          content={
            <Select
              name="locale"
              value={props.locale}
              onChange={props.onChange}
              options={props.locales}
            />
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['locale'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('locale')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      <Block>
        <ExpandFormField
          isOpen={props.expanded === 'color'}
          label={t('color')}
          name="color"
          description={t('color description')}
          onExpand={props.onExpand}
          summary={
            props.color && <ColorButton color={props.color} isDisabled />
          }
          content={
            <>
              <BasicColorContainer>
                <ColorListTitle>{t('basic color')}</ColorListTitle>
                {props.color && (
                  <ColorList
                    colors={props.colors}
                    color={props.color}
                    onChange={props.onChange}
                  />
                )}
              </BasicColorContainer>
              <ColorListTitle>{t('custom color')}</ColorListTitle>
              {props.color && (
                <OriginalColorPicker
                  color={props.color}
                  originalColor={props.originalColor}
                  onChange={props.onChange}
                  onChangeOriginalColor={props.onChangeOriginalColor}
                />
              )}
            </>
          }
          submitField={
            <Button
              isLoading={props.isLoading}
              isDisabled={props.errors['color'] ? true : false}
              onClick={(e: SyntheticEvent) => props.onSubmit('color')}
              color="primary"
            >
              {t('Save')}
            </Button>
          }
        />
      </Block>
      {timeZoneAnchor && (
        <TimeZoneListPopover
          title={t('timeZone')}
          anchor={timeZoneAnchor}
          onClose={onCloseTimeZone}
          onSelect={onSelectTimeZone}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}
    </Container>
  );
}

const Block = styled.div`
  margin-bottom: ${spacing(40)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const Container = styled.div``;

const UploadAvatar = styled.div`
  position: relative;
  color: ${white};
  width: 80px;
  height: 80px;
  background-color: ${gray03};
  border-radius: 40px;
`;
const UploadIcon = styled(Icon)`
  position: absolute;
  top: calc(50% - 12px);
  left: calc(50% - 12px);
`;

const FileInput = styled.input`
  display: none;
`;
const ProfileImage = styled.div`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  margin-right: ${spacing(48)};
  position: relative;
  width: 80px;
  height: 80px;
`;

const DeleteIconButton = styled.div`
  color: ${white};
  background-color: ${gray03};
  font-size: 12px;
  display: flex;
  padding: ${spacing(4)};
  border-radius: ${borderRadius()};
`;

const BasicColorContainer = styled.div`
  width: fit-content;
  padding-bottom: ${spacing(16)};
  border-bottom: 1px dashed ${gray03};
  margin-bottom: ${spacing(16)};
`;

const ColorListTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(12)};
    font-size: ${fontSize(10)};
  }
`;

const FieldWithPrefix = styled.div`
  display: flex;
  align-items: center;
`;
const Prefix = styled.div`
  margin-right: ${spacing(8)};
`;

const TimeZoneContainer = styled.div`
  display: inline-flex;
  cursor: pointer;
  :hover {
    opacity: 0.8;
  }
`;
const TimeZoneSelect = styled(Select)`
  user-select: none;
  pointer-events: none;
`;
